import { ThemedText } from "@/components/themed-text";
import {
  AuthDesktopCard,
  AuthDesktopHeader,
  AuthDesktopLayout,
  AuthScreenContent,
  AuthScreenFooter,
  AuthScreenHeader,
  AuthScreenLayout,
  AuthScreenSpacer,
  authScreenStyles,
} from "@/components/auth/auth-screen-layout";
import { BrandButton } from "@/components/ui/brand-button";
import { OAuth } from "@/components/ui/oauth";
import { BrandColors, Colors } from "@/constants/theme";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import {
  registrationTypeToRole,
  REGISTRATION_LABELS,
  type RegistrationType,
} from "@/lib/roles";
import Ionicons from "@react-native-vector-icons/ionicons";
import MaterialCommunityIcons from "@react-native-vector-icons/material-design-icons";
import { Link, router, useLocalSearchParams } from "expo-router";
import React, { useState } from "react";
import {
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";

const accountTypes: {
  id: RegistrationType;
  title: string;
  description: string;
  renderIcon: (desktop: boolean) => React.ReactNode;
}[] = [
  {
    id: "private_customer",
    title: REGISTRATION_LABELS.private_customer,
    description: "Book transport for personal use.",
    renderIcon: (desktop) => (
      <Ionicons
        name="person-outline"
        size={desktop ? 18 : 28}
        color="#4B5E6E"
      />
    ),
  },
  {
    id: "business_customer",
    title: REGISTRATION_LABELS.business_customer,
    description: "Book transport for your company or fleet.",
    renderIcon: (desktop) => (
      <Ionicons
        name={desktop ? "briefcase-outline" : "business-outline"}
        size={desktop ? 18 : 28}
        color="#4B5E6E"
      />
    ),
  },
  {
    id: "operator",
    title: REGISTRATION_LABELS.operator,
    description: "Accept jobs and grow your transport business.",
    renderIcon: (desktop) => (
      <MaterialCommunityIcons
        name="car-outline"
        size={desktop ? 18 : 28}
        color="#4B5E6E"
      />
    ),
  },
];

export default function RegisterScreen() {
  const { ref } = useLocalSearchParams<{ ref?: string }>();
  const desktop = useDesktopLayout();
  const [selectedType, setSelectedType] =
    useState<RegistrationType>("private_customer");

  const handleBack = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/(auth)");
    }
  };

  const handleContinue = () => {
    router.push({
      pathname: "/(auth)/register-details",
      params: {
        registration_type: selectedType,
        role: registrationTypeToRole(selectedType),
        ...(ref ? { ref } : {}),
      },
    });
  };

  const platformList = (
    <View style={[styles.platformList, desktop && styles.desktopPlatformList]}>
      {accountTypes.map((accountType) => {
        const isSelected = selectedType === accountType.id;

        return (
          <TouchableOpacity
            key={accountType.id}
            style={[
              styles.platformCard,
              desktop && styles.desktopPlatformCard,
              accountType.id === "operator" && styles.platformCardFull,
              { borderColor: BrandColors.mediumGray },
              isSelected && styles.platformCardSelected,
            ]}
            onPress={() => setSelectedType(accountType.id)}
            activeOpacity={0.8}
            accessibilityRole="radio"
            accessibilityState={{ selected: isSelected }}
            accessibilityLabel={accountType.title}
          >
            <View style={desktop ? styles.desktopPlatformIcon : styles.platformIcon}>
              {accountType.renderIcon(desktop)}
            </View>

            <ThemedText
              style={[styles.platformTitle, desktop && styles.desktopPlatformTitle, isSelected && {color: BrandColors.charcoal}]}
              type="smallSemiBold"
            >
              {accountType.title}
            </ThemedText>

            <ThemedText
              style={[
                styles.platformDescription,
                desktop && styles.desktopPlatformDescription,
                isSelected && { color: BrandColors.charcoal },
              ]}
            >
              {accountType.description}
            </ThemedText>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const continueButton = (
    <BrandButton
      variant="primary"
      onPress={handleContinue}
      style={desktop ? authScreenStyles.desktopContinueButton : authScreenStyles.continueButton}
      labelStyle={
        desktop ? authScreenStyles.desktopContinueButtonLabel : authScreenStyles.continueButtonLabel
      }
      contentStyle={authScreenStyles.continueButtonContent}
      icon={() => (
        <Ionicons
          name="chevron-forward"
          size={desktop ? 14 : 18}
          color={BrandColors.white}
        />
      )}
    >
      Continue
    </BrandButton>
  );

  const loginFooter = (
    <AuthScreenFooter>
      <ThemedText style={authScreenStyles.footerText} type="small">
        Already have an account?{" "}
      </ThemedText>
      <Link href="/(auth)" asChild>
        <TouchableOpacity accessibilityLabel="goto-login">
          <ThemedText style={authScreenStyles.link} type="smallSemiBold">
            Login
          </ThemedText>
        </TouchableOpacity>
      </Link>
    </AuthScreenFooter>
  );

  if (desktop) {
    return (
      <AuthDesktopLayout onBack={handleBack}>
        <AuthDesktopHeader
          title="Create Account"
          subtitle="Sign up to get started with Auto Transporter UK"
        />
        <AuthDesktopCard>
          <ThemedText style={styles.desktopPlatformLabel}>
            Choose how you&apos;ll use the platform
          </ThemedText>
          {platformList}
          <OAuth
            variant="labeled"
            dividerLabel="Or sign up with"
            role={registrationTypeToRole(selectedType)}
          />
          {continueButton}
          {loginFooter}
        </AuthDesktopCard>
      </AuthDesktopLayout>
    );
  }

  return (
    <AuthScreenLayout onBack={handleBack}>
      <AuthScreenContent>
        <AuthScreenHeader
          title="Create Account"
          subtitle="Sign up to get started"
        />

        <View style={styles.platformSection}>
          <ThemedText style={styles.platformLabel}>
            Choose how you&apos;ll use the platform?
          </ThemedText>

          {platformList}
        </View>
      </AuthScreenContent>

      <AuthScreenSpacer />

      <AuthScreenContent>{continueButton}</AuthScreenContent>

      {loginFooter}
    </AuthScreenLayout>
  );
}

const styles = StyleSheet.create({
  platformSection: {
    gap: 16,
  },
  platformLabel: {
    fontSize: 16,
    fontWeight: "600",
  },
  platformList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  platformCard: {
    width: "48%",
    paddingHorizontal: 12,
    paddingVertical: 20,
    borderRadius: 16,
    borderWidth: 1,
    gap: 10,
    minHeight: 168,
  },
  platformCardFull: {
    width: "100%",
  },
  platformCardSelected: {
    borderColor: BrandColors.primary,
    backgroundColor: BrandColors.primaryLight,
  },
  platformIcon: {
    marginBottom: 4,
  },
  platformTitle: {
    fontWeight: "700",
  },
  platformDescription: {
    fontSize: 12,
    lineHeight: 16,
  },
  desktopPlatformLabel: {
    fontSize: 13,
    lineHeight: 18,
    color: BrandColors.mediumGray,
    marginBottom: 12,
  },
  desktopPlatformList: {
    gap: 8,
  },
  desktopPlatformCard: {
    flexBasis: "40%",
    flexGrow: 1,
    minHeight: 100,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: 8,
    gap: 4,
  },
  desktopPlatformIcon: {
    marginBottom: 6,
  },
  desktopPlatformTitle: {
    fontSize: 13,
    lineHeight: 18,
  },
  desktopPlatformDescription: {
    fontSize: 11,
    lineHeight: 15,
  },
});
